import type { Step } from 'react-joyride'

export interface TourStep extends Step {
  route?: string // navigate to this route before showing the step
}

export type TourId = 'getting_started' | 'live_class' | 'reports' | 'refer_friend'

interface TourConfig {
  id: TourId
  title: string
  description: string
  credits: number
  steps: TourStep[]
}

const GETTING_STARTED_STEPS: TourStep[] = [
  {
    target: 'body',
    title: 'Welcome, Captain!',
    content: "Welcome to The Armada! Let's walk through the key features so you can start teaching in minutes.",
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="nav-courses"]',
    title: 'Course Catalog',
    content: 'Browse ready-to-run courses here. Each course contains lessons, activities, and assessments.',
    route: '/dashboard',
    disableBeacon: true,
  },
  {
    target: '[data-tour="course-catalog"]',
    title: 'Browse Courses',
    content: 'Each card shows lessons, duration, and pricing. Click any course to explore its content.',
    route: '/courses',
    disableBeacon: true,
  },
  {
    target: '[data-tour="nav-my-courses"]',
    title: 'My Courses',
    content: "Your courses live here. We've set up a free demo course for you!",
    disableBeacon: true,
  },
  {
    target: '[data-tour="my-course-card"]',
    title: 'Your Demo Course',
    content: 'Open your demo course to see its lessons and create a run.',
    route: '/my-courses',
    disableBeacon: true,
  },
  {
    // target will be dynamically set by TourProvider using the demo course ID
    target: '[data-tour="new-run-btn"]',
    title: 'Create a Run',
    content: 'Create a "Run" to teach your class. Each run gets a unique join code for students.',
    // route is set dynamically in TourProvider
    disableBeacon: true,
  },
  {
    target: '[data-tour="lesson-list"]',
    title: 'Lesson List',
    content: 'Each run has lessons you launch one at a time. Students join with the run code and follow along.',
    disableBeacon: true,
  },
  {
    target: '[data-tour="reports-tab"]',
    title: 'Reports & Analytics',
    content: 'View scores, response rates, and export data to Excel or PDF.',
    disableBeacon: true,
  },
  {
    target: 'body',
    title: 'You earned 5 credits!',
    content: "Great job completing the Getting Started tour! 5 credits have been added to your account.",
    placement: 'center',
    disableBeacon: true,
  },
]

const LIVE_CLASS_STEPS: TourStep[] = [
  {
    target: 'body',
    title: "Let's Run a Live Class!",
    content: "Now let's explore the host dashboard where you control live lessons and monitor student participation.",
    placement: 'center',
    disableBeacon: true,
  },
  {
    // Route is set dynamically in TourProvider — may include payment step first
    target: '[data-tour="start-lesson-btn"]',
    title: 'Start Your Lesson',
    content: 'Click the Start Lesson button to go live! Choose "Live (Instructor-Led)" mode when prompted.',
    disableBeacon: true,
    hideFooter: true,
    spotlightClicks: true,
  },
  {
    target: '[data-tour="lesson-master-header"]',
    title: 'Host Dashboard',
    content: 'This is your command center during a live lesson. You can see the lesson status, student count, and join code.',
    disableBeacon: true,
  },
  {
    target: '[data-tour="lesson-controls"]',
    title: 'Lesson Controls',
    content: 'Pause activities, toggle audio, and end the lesson from these controls.',
    disableBeacon: true,
  },
  {
    target: '[data-tour="activity-controller"]',
    title: 'Activity Controls',
    content: 'Launch activities one by one. Each activity has its own timer, responses, and scoring.',
    disableBeacon: true,
  },
  {
    target: '[data-tour="analytics-tab"]',
    title: 'Live Analytics',
    content: 'Monitor student responses, scores, and participation in real time while the lesson runs.',
    disableBeacon: true,
  },
  {
    target: '[data-tour="present-btn"]',
    title: 'Presenter Mode',
    content: 'Click "Present" to open a full-screen student view in a new tab — perfect for projecting to the class.',
    disableBeacon: true,
  },
  {
    target: 'body',
    title: 'You earned 5 credits!',
    content: "Excellent! You've mastered the live class controls. 5 credits have been added to your account. Now try running through these activities!",
    placement: 'center',
    disableBeacon: true,
  },
]

const REPORTS_STEPS: TourStep[] = [
  {
    target: 'body',
    title: "Let's Explore Reports!",
    content: "Finally, let's see how to analyze your course and lesson data with detailed reports.",
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="reports-tab"]',
    title: 'Course Reports',
    content: 'Access reports from the Reports tab on any course detail page.',
    // route is set dynamically in TourProvider
    disableBeacon: true,
  },
  {
    target: '[data-tour="course-report-summary"]',
    title: 'Report Summary',
    content: 'See high-level stats: total runs, average students per run, duration, and response rates.',
    // route is set dynamically in TourProvider
    disableBeacon: true,
  },
  {
    target: '[data-tour="course-report-runs"]',
    title: 'Run Breakdown',
    content: 'Drill down into individual runs to compare performance across different cohorts.',
    disableBeacon: true,
  },
  {
    target: '[data-tour="run-report-summary"]',
    title: 'Run Report',
    content: 'Each run report shows student counts, activities completed, response rates, and scores.',
    // route is set dynamically in TourProvider
    disableBeacon: true,
  },
  {
    target: '[data-tour="lesson-report-header"]',
    title: 'Lesson Report',
    content: 'Lesson-level reports show the most detail: per-student scores, activity breakdowns, and response timelines.',
    // route is set dynamically in TourProvider
    disableBeacon: true,
  },
  {
    target: '[data-tour="report-export"]',
    title: 'Export Data',
    content: 'Use the Reports tab to export data to Excel or PDF for sharing with stakeholders or archiving results.',
    disableBeacon: true,
  },
  {
    target: 'body',
    title: 'You earned 5 credits!',
    content: "You've completed all onboarding tours! 5 credits have been added. You're now a fully equipped captain.",
    placement: 'center',
    disableBeacon: true,
  },
]

export const TOURS: Record<TourId, TourConfig> = {
  getting_started: {
    id: 'getting_started',
    title: 'Getting Started',
    description: 'Browse courses, explore your demo course, and create a run.',
    credits: 5,
    steps: GETTING_STARTED_STEPS,
  },
  live_class: {
    id: 'live_class',
    title: 'Running a Live Class',
    description: 'Learn the host dashboard, lesson controls, and live analytics.',
    credits: 5,
    steps: LIVE_CLASS_STEPS,
  },
  reports: {
    id: 'reports',
    title: 'Reports & Analytics',
    description: 'Explore course, run, and lesson reports, and export data.',
    credits: 5,
    steps: REPORTS_STEPS,
  },
  refer_friend: {
    id: 'refer_friend',
    title: 'Refer a Friend',
    description: 'Share your referral code and earn credits when a friend signs up.',
    credits: 5,
    steps: [], // not a guided tour — completed via referral system
  },
}

export const TOUR_ORDER: TourId[] = ['getting_started', 'live_class', 'reports', 'refer_friend']

// Keep TOUR_STEPS as alias for backward compatibility
const TOUR_STEPS = GETTING_STARTED_STEPS
